import React from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { AppButton } from '@pro-sto/ui';
import { ExclamationMarkIcon } from '@/components/icons';
import { operatingHoursStore, shiftsStore } from '@/stores';
import type { ShiftDay } from '@/types/shift.types';
import {
  dayTitle,
  findShiftForDate,
  formatPlannedTime,
  shiftIntervalLabel,
  shiftStatusLabel,
  showWeekCreateShift,
  toDateParam,
  weekDays,
  weekdayLabel,
} from './shiftWeekModel';
import './ShiftsWeek.css';

interface ShiftsWeekProps {
  weekStart: Date;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  onCreateShift: (date: Date) => void;
  onRetry: () => void;
}

const PlusCircleIcon: React.FC = () => (
  <span className="shifts-week__plus" aria-hidden="true">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M6 2.25V9.75M2.25 6H9.75"
        stroke="#ffffff"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

const ShiftsWeek: React.FC<ShiftsWeekProps> = observer(({
  weekStart,
  selectedDate,
  onSelectDate,
  onCreateShift,
  onRetry,
}) => {
  const showError = Boolean(shiftsStore.loadError) && !shiftsStore.isLoading;
  const showSkeleton = !showError && shiftsStore.loadedFrom === null;

  if (showError) {
    return (
      <div className="shifts-week__error" data-testid="shifts-error">
        <ExclamationMarkIcon size={24} />
        <p className="shifts-week__error-message" title={shiftsStore.loadError ?? undefined}>
          {shiftsStore.loadError}
        </p>
        <AppButton variant="secondary" onClick={onRetry} data-testid="shifts-retry">
          Повторить
        </AppButton>
      </div>
    );
  }

  if (showSkeleton) {
    return (
      <div className="shifts-week" data-testid="shifts-loading">
        <div className="shifts-week__grid">
          {Array.from({ length: 7 }).map((_, index) => (
            <div className="shifts-week__column" key={index}>
              <span className="shifts-week__skeleton shifts-week__skeleton_header" />
              <div className="shifts-week__cards">
                <span className="shifts-week__skeleton shifts-week__skeleton_card" />
                <span className="shifts-week__skeleton shifts-week__skeleton_card" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const days = weekDays(weekStart);
  const selectedKey = toDateParam(selectedDate);
  const timeZone = operatingHoursStore.timezone || 'Europe/Moscow';

  return (
    <div className="shifts-week" data-testid="shifts-week">
      <div className="shifts-week__grid">
        {days.map((day) => {
          const dateKey = toDateParam(day);
          const shift = findShiftForDate(shiftsStore.shifts, day);
          const selected = dateKey === selectedKey;
          return (
            <ShiftColumn
              key={dateKey}
              day={day}
              dateKey={dateKey}
              shift={shift}
              selected={selected}
              interval={shiftIntervalLabel(
                shift,
                day,
                operatingHoursStore.regularSchedule,
                operatingHoursStore.specialDates,
                timeZone,
              )}
              onSelectDate={onSelectDate}
              onCreateShift={onCreateShift}
            />
          );
        })}
      </div>
    </div>
  );
});

interface ShiftColumnProps {
  day: Date;
  dateKey: string;
  shift: ShiftDay | undefined;
  selected: boolean;
  interval: string;
  onSelectDate: (date: Date) => void;
  onCreateShift: (date: Date) => void;
}

const ShiftColumn: React.FC<ShiftColumnProps> = ({
  day,
  dateKey,
  shift,
  selected,
  interval,
  onSelectDate,
  onCreateShift,
}) => {
  const status = shiftStatusLabel(shift);
  const showCreate = showWeekCreateShift(shift);

  return (
    <div className="shifts-week__column" data-testid={`shifts-column-${dateKey}`}>
      <button
        type="button"
        className={classNames('shifts-week__day', {
          'shifts-week__day_selected': selected,
        })}
        aria-current={selected ? 'date' : undefined}
        onClick={() => onSelectDate(day)}
        data-testid={`shifts-day-${dateKey}`}
      >
        <span className="shifts-week__day-title">
          <span>{weekdayLabel(day)}</span>
          <span>{dayTitle(day)}</span>
        </span>
        <span className="shifts-week__day-meta">
          {interval ? <span className="shifts-week__day-interval">{interval}</span> : null}
          <span
            className={classNames('shifts-week__status', {
              'shifts-week__status_open': shift?.status === 'open',
            })}
            title={status}
            data-testid={`shifts-status-${dateKey}`}
          >
            {status}
          </span>
        </span>
      </button>

      {showCreate ? (
        <button
          type="button"
          className="shifts-week__create"
          onClick={() => onCreateShift(day)}
          data-testid={`shifts-create-${dateKey}`}
        >
          <span>Создать смену</span>
          <PlusCircleIcon />
        </button>
      ) : (
        <div className="shifts-week__cards">
          {shift?.members.map((member) => {
            const planned = formatPlannedTime(member.planned_start, member.planned_end);
            return (
              <div
                className="shifts-week__card"
                key={member.uuid}
                data-testid={`shift-card-${member.uuid}`}
              >
                <div className="shifts-week__card-person">
                  <p className="shifts-week__card-name" title={member.name}>
                    {member.name}
                  </p>
                  {member.position ? (
                    <p className="shifts-week__card-role" title={member.position}>
                      {member.position}
                    </p>
                  ) : null}
                </div>
                {planned ? (
                  <p className="shifts-week__card-time" title={planned}>
                    {planned}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

ShiftsWeek.displayName = 'ShiftsWeek';

export default ShiftsWeek;
